import { useState, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import IconClose from "@/components/Icons/V2/Edit/close.svg?react";
import {
  StyledActionsWrapper,
  StyledBodyInfoModal,
  StyledNotificationBox,
  StyledPrimaryActionsWrapper,
  StyledWrapperContent,
  StyledWrapperFooterModal,
  StyledWrapperRight,
} from "../AuthStepsModal/styles";
import tw from "twin.macro";
import Heading from "@/components/Atoms/Heading";
import Paragraph from "@/components/Atoms/Paragraph";
import Input from "@/components/Molecules/Input";
import Button from "@/components/Atoms/Button";
import IconAuth from "@/components/Atoms/IconAuth";
import TogglePasswordInput from "@/components/Atoms/TogglePasswordInput";
import Notification from "@/components/Atoms/Notification";
import StepOrBackHeader from "@/components/Atoms/StepOrBackHeader";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { type StepAuth } from "../AuthStepsModal";
import { loginUser } from "@/services/auth";
import Analytics from "@/utils/Analytics";
import useSessionContext from "@/hooks/useSesionContext";
import useModalAuth from "@/hooks/useModalAuth";
import { identify, login as loginAnalytics } from "@utils/analyticsV2";
import { useTurnstile } from "@/hooks/useTurnstile";
import { parseError } from "@/utils/errorHandling";

interface LoginProps {
  onClose: () => void;
  setStep: React.Dispatch<React.SetStateAction<StepAuth>>;
  title?: string;
  isMobile?: boolean;
  updateCurrentUser: (data: Omit<UserAttr, "token">) => void;
  onSuccess?: () => void;
}

interface LoginFormFields {
  email: string;
  password: string;
}

const Login = ({ onClose, setStep, isMobile, updateCurrentUser, onSuccess }: LoginProps) => {
  const isLarge = useMediaQuery("(min-width: {large})");
  const { setUserToken } = useSessionContext();
  const { handleCallback } = useModalAuth();
  const [fetchingError, setFetchingError] = useState<string[] | string>([]);
  const [requiresCaptcha, setRequiresCaptcha] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<LoginFormFields>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {},
  });

  const cleanErrors = () => setFetchingError([]);

  const {
    containerRef,
    isReady,
    error: turnstileError,
    reset,
  } = useTurnstile({
    action: "login",
    enabled: requiresCaptcha,
    onSuccess: useCallback((token: string) => {
      setRecaptchaToken(token);
    }, []),
    onError: useCallback(() => {
      setRecaptchaToken(null);
    }, []),
  });

  const login = useMutation({
    mutationFn: loginUser,
    mutationKey: ["login"],
    onSuccess: async ({ token, user }) => {
      setFetchingError([]);
      if (onSuccess) {
        onSuccess();
        loginAnalytics.loginBasic({ userId: String(user?.id) });
      }
      updateCurrentUser(user);
      setUserToken(token);
      handleCallback();

      // if (location.test) emptyLocation();

      // Identify users in all analytics tools
      await identify({
        id: String(user.id),
        user: {
          email: user.email,
          firstName: `${user.first_name}`,
          lastName: `${user.last_name}`,
          phone: user.telephone,
          createdAt: user.date_joined,
          membership: user.membership
            ? {
                code: user.membership.code,
                planId: user.membership.plan,
                planName: user.membership.plan_name,
                status: user.membership.status,
              }
            : null,
        },
      });
      onClose();
    },
    onError: (error: any) => {
      const parsedError = parseError(error);
      setFetchingError(parsedError);

      // check if requires captcha
      const errorData = error?.data;
      if (errorData?.requires_recaptcha === "True") {
        setRequiresCaptcha(true);
      }

      // reset recaptcha if it was required
      if (requiresCaptcha) {
        reset();
        setRecaptchaToken(null);
      }

      const userAgent = navigator.userAgent;
      const { email, password } = getValues();
      Analytics.LoginAttempt({ agent: userAgent, email, password });
    },
  });

  const onSubmit = async ({ email, password }: LoginFormFields) => {
    try {
      const payload: any = { email, password };

      if (requiresCaptcha) {
        if (!recaptchaToken) {
          setFetchingError(["Por favor completa la verificación de seguridad"]);
          return;
        }
        payload.recaptchaToken = recaptchaToken;
      }
      await login.mutateAsync(payload);
    } catch (error) {}
  };

  const { onChange: emailOnChange, ...emailRegister } = register("email", {
    required: "Necesitamos tu correo",
  });
  const { onChange: passwordOnChange, ...passwordRegister } = register("password", {
    required: "Necesitamos tu contraseña",
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} tw="h-full">
      <StyledWrapperRight>
        <StyledWrapperContent>
          {!isMobile && <IconClose tw="h-ml w-ml justify-self-end cursor-pointer" onClick={onClose} />}
          {isMobile && (
            <div tw="-mt-s -mx-m">
              <StepOrBackHeader onClose={onClose} />
            </div>
          )}
          <IconAuth type="user" />

          <StyledBodyInfoModal>
            <Heading type="h2">Hola</Heading>
            <Paragraph sizeVariant="md">
              Ingresa a tu cuenta y compra de manera rápida, fácil y segura los productos que evolucionan tus
              hábitos.
            </Paragraph>
          </StyledBodyInfoModal>

          <Input
            label="Correo electrónico"
            placeholder="Ingresa tu correo electrónico"
            type="email"
            errors={Boolean(errors?.email?.message) ?? undefined}
            size={isLarge ? "large" : "medium"}
            {...emailRegister}
            onChange={(e) => {
              void emailOnChange(e);
              cleanErrors();
            }}
          />

          <TogglePasswordInput
            label="Contraseña"
            errors={Boolean(errors?.password?.message)}
            placeHolder="Ingresa tu contraseña"
            size={isLarge ? "large" : "medium"}
            {...passwordRegister}
            onChange={(e) => {
              void passwordOnChange(e);
              cleanErrors();
            }}
          />

          {/*Widget de Turnstile */}
          {requiresCaptcha && (
            <div>
              <div ref={containerRef} tw="flex justify-center min-h-[65px]" />
              {turnstileError && (
                <Paragraph sizeVariant="sm" tw="text-red-500 text-center mt-2">
                  {turnstileError}
                </Paragraph>
              )}
              {!isReady && !turnstileError && (
                <Paragraph sizeVariant="sm" tw="text-gray-500 text-center mt-2">
                  Cargando verificación...
                </Paragraph>
              )}
            </div>
          )}
        </StyledWrapperContent>

        <StyledActionsWrapper>
          <StyledPrimaryActionsWrapper>
            <Button
              sizeVariant="lg"
              color="secondary"
              type="submit"
              disabled={login.isPending || (requiresCaptcha && !recaptchaToken)}
              fullWidth
            >
              Iniciar sesión
            </Button>
            <Paragraph
              sizeVariant="sm"
              classes={tw`underline cursor-pointer self-center`}
              onClick={() => setStep("phoneNumberPassword")}
            >
              ¿Olvidaste tu contraseña?
            </Paragraph>
          </StyledPrimaryActionsWrapper>

          <StyledNotificationBox visible={!!fetchingError && fetchingError?.length > 0}>
            <Notification type="error" messages={fetchingError} hFull />
          </StyledNotificationBox>

          <StyledWrapperFooterModal>
            <Paragraph sizeVariant="sm">¿No tienes cuenta con nosotros?</Paragraph>
            <Button
              sizeVariant="sm"
              variant="text"
              color="secondary"
              type="button"
              onClick={() => setStep("phoneNumber")}
            >
              Crear cuenta
            </Button>
          </StyledWrapperFooterModal>
        </StyledActionsWrapper>
      </StyledWrapperRight>
    </form>
  );
};

export default Login;
